'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import en from './en.json';
import zh from './zh.json';

export type Lang = 'en' | 'zh';
export type Theme = 'dark' | 'light';

const messages: Record<Lang, Record<string, unknown>> = { en, zh };

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
}

interface ThemeCtx {
  theme: Theme;
  toggleTheme: () => void;
}

const I18nContext = createContext<I18nCtx | null>(null);
const ThemeContext = createContext<ThemeCtx | null>(null);

export function AppProviders({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>('en');
  const [theme, setTheme] = useState<Theme>('dark');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedLang = typeof window !== 'undefined' ? (localStorage.getItem('wmadmin-lang') as Lang | null) : null;
    if (savedLang === 'en' || savedLang === 'zh') setLangState(savedLang);
    const savedTheme = typeof window !== 'undefined' ? (localStorage.getItem('wmadmin-theme') as Theme | null) : null;
    if (savedTheme === 'light' || savedTheme === 'dark') setTheme(savedTheme);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.lang = lang === 'en' ? 'en' : 'zh-CN';
    localStorage.setItem('wmadmin-theme', theme);
    localStorage.setItem('wmadmin-lang', lang);
  }, [theme, lang, mounted]);

  const setLang = (l: Lang) => setLangState(l);

  const t = (key: string): string => {
    const parts = key.split('.');
    let obj: unknown = messages[lang];
    for (const p of parts) {
      if (obj && typeof obj === 'object' && p in (obj as Record<string, unknown>)) {
        obj = (obj as Record<string, unknown>)[p];
      } else {
        return key;
      }
    }
    return typeof obj === 'string' ? obj : key;
  };

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nCtx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within AppProviders');
  return ctx;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within AppProviders');
  return ctx;
}
